import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { cn } from '@/lib/cn';

/*
 * Drawn-bitmap signature capture. A completed signature is always the
 * drawn ink — name/role in the dropdown alone doesn't count, per the
 * 2026-04-19 design decision. The parent reads out the current bitmap
 * via the forwarded ref (`toDataUrl()` / `isBlank()`) when saving.
 *
 * Pointer events cover mouse, touch, and pen in a single code path,
 * sidestepping the legacy app's separate mouse-/touch-handler split.
 * CSS sizing (width/height) drives the canvas's bounding box; an
 * internal coordinate scale keeps the drawn line crisp on a retina
 * display without resizing the DOM element.
 */

export interface SignatureCanvasHandle {
    toDataUrl(): string;
    clear(): void;
    isBlank(): boolean;
    loadFromDataUrl(dataUrl: string): void;
}

interface SignatureCanvasProps {
    /** Forwarded handle for imperative access from the parent. */
    disabled?: boolean;
    /**
     * Initial image to render — used when re-opening a note that already
     * had a signature drawn. Subsequent strokes paint over it.
     */
    initialDataUrl?: string | null;
    /** Fired whenever a stroke ends, so parents can dirty-state-track. */
    onChange?: () => void;
    className?: string;
}

/** Physical pixel density for the off-screen buffer. Higher values sharpen
 *  strokes on retina displays; anything above 2× is wasted. */
const DEVICE_SCALE = 2;
const BUFFER_WIDTH  = 560 * DEVICE_SCALE;
const BUFFER_HEIGHT = 110 * DEVICE_SCALE;

export const SignatureCanvas = forwardRef<SignatureCanvasHandle, SignatureCanvasProps>(
    ({ disabled, initialDataUrl, onChange, className }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement | null>(null);
        const drawingRef = useRef(false);
        const blankRef = useRef(true);

        function ctx() {
            const c = canvasRef.current;
            if (!c) return null;
            return c.getContext('2d');
        }

        function clearInternal() {
            const c = canvasRef.current;
            const g = ctx();
            if (!c || !g) return;
            g.clearRect(0, 0, c.width, c.height);
            blankRef.current = true;
        }

        function loadFromDataUrl(dataUrl: string) {
            const c = canvasRef.current;
            const g = ctx();
            if (!c || !g || !dataUrl) return;
            const img = new Image();
            img.onload = () => {
                g.clearRect(0, 0, c.width, c.height);
                g.drawImage(img, 0, 0, c.width, c.height);
                blankRef.current = false;
            };
            img.src = dataUrl;
        }

        useImperativeHandle(ref, () => ({
            toDataUrl: () => {
                const c = canvasRef.current;
                if (!c) return '';
                if (blankRef.current) return '';
                return c.toDataURL('image/png');
            },
            clear: () => {
                clearInternal();
                onChange?.();
            },
            isBlank: () => blankRef.current,
            loadFromDataUrl,
        }), [onChange]);

        useEffect(() => {
            if (initialDataUrl) loadFromDataUrl(initialDataUrl);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [initialDataUrl]);

        function posFromEvent(e: React.PointerEvent<HTMLCanvasElement>) {
            const c = canvasRef.current!;
            const rect = c.getBoundingClientRect();
            return {
                x: (e.clientX - rect.left) * (c.width  / rect.width),
                y: (e.clientY - rect.top)  * (c.height / rect.height),
            };
        }

        function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
            if (disabled) return;
            const g = ctx();
            if (!g) return;
            drawingRef.current = true;
            (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
            const p = posFromEvent(e);
            g.beginPath();
            g.moveTo(p.x, p.y);
        }

        function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
            if (!drawingRef.current || disabled) return;
            const g = ctx();
            if (!g) return;
            const p = posFromEvent(e);
            g.lineTo(p.x, p.y);
            g.strokeStyle = '#111';
            g.lineWidth = 3;
            g.lineCap = 'round';
            g.lineJoin = 'round';
            g.stroke();
            blankRef.current = false;
        }

        function onPointerEnd() {
            if (!drawingRef.current) return;
            drawingRef.current = false;
            onChange?.();
        }

        return (
            <canvas
                ref={canvasRef}
                width={BUFFER_WIDTH}
                height={BUFFER_HEIGHT}
                className={cn(
                    'w-full bg-surface border border-border rounded-md touch-none',
                    disabled && 'opacity-60 cursor-not-allowed',
                    !disabled && 'cursor-crosshair',
                    className,
                )}
                style={{ height: 110 }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                onPointerLeave={onPointerEnd}
            />
        );
    },
);
SignatureCanvas.displayName = 'SignatureCanvas';
